import React, { useState } from 'react';
import { T, useT } from '@transifex/react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

export type OtpStepProps = {
  email: string;
  onVerify: (code: string) => Promise<void>;
  onBack?: () => void;
  hint?: React.ReactNode;
};

export const OtpStep: React.FC<OtpStepProps> = ({
  email,
  onVerify,
  onBack,
  hint,
}) => {
  const tx = useT();
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [verifying, setVerifying] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!/^\d{6}$/.test(code)) {
      setError(tx('Code must be 6 digits'));
      return;
    }
    setVerifying(true);
    try {
      await onVerify(code);
    } catch {
      setError(tx('Invalid code'));
    } finally {
      // Reset on success too: OtpStep is shared, and a consumer whose onVerify
      // resolves without navigating away must not be stuck on a disabled
      // "Verifying…" button.
      setVerifying(false);
    }
  };

  return (
    <div className="wh-ui w-full">
      <form onSubmit={submit} className="flex w-full flex-col gap-4">
        <div className="grid gap-2">
          <label
            htmlFor="otp-code"
            className="text-sm font-medium leading-none"
          >
            <T _str="Enter the 6-digit code sent to {email}" email={email} />
          </label>
          <Input
            id="otp-code"
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            placeholder="123456"
            value={code}
            onChange={(e) => {
              setCode(e.target.value);
              if (error) setError(null);
            }}
          />
          {error && <p className="text-sm text-destructive">{error}</p>}
        </div>
        {hint}
        <Button type="submit" disabled={verifying} className="h-12 w-full">
          {verifying ? <T _str="Verifying…" /> : <T _str="Verify" />}
        </Button>
        {onBack && (
          <Button
            type="button"
            variant="ghost"
            onClick={onBack}
            className="h-12 w-full"
          >
            <T _str="Use a different email" />
          </Button>
        )}
      </form>
    </div>
  );
};
